import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GrokSentimentData, GrokSentimentResponse } from '../../types';

interface GrokSentimentState {
  byTicker: Record<string, GrokSentimentData & {
    success: boolean;
    error?: string;
    is_cached?: boolean;
    analyzed_at?: string;
    cache_age_hours?: number;
  }>;
  loading: boolean;
  batchLoading: boolean;
  error: string | null;
  lastUpdated: Record<string, number>; // Timestamp per ticker
}

// LocalStorage key for persistence
const STORAGE_KEY = 'grokSentiment';
const CACHE_TTL_HOURS = 24;

// Load state from localStorage with TTL validation
const loadFromLocalStorage = (): Partial<GrokSentimentState> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};

    const parsed = JSON.parse(stored);
    const now = Date.now();
    const ttlMs = CACHE_TTL_HOURS * 60 * 60 * 1000;

    // Filter out stale data (> 24h old)
    const filteredByTicker: typeof parsed.byTicker = {};
    const filteredLastUpdated: typeof parsed.lastUpdated = {};

    Object.entries(parsed.byTicker || {}).forEach(([ticker, data]: [string, any]) => {
      const analyzedAt = data.analyzed_at ? new Date(data.analyzed_at).getTime() : 0;
      const age = now - analyzedAt;

      if (age < ttlMs) {
        filteredByTicker[ticker] = data;
        if (parsed.lastUpdated?.[ticker]) {
          filteredLastUpdated[ticker] = parsed.lastUpdated[ticker];
        }
      }
    });

    return {
      byTicker: filteredByTicker,
      lastUpdated: filteredLastUpdated,
    };
  } catch (error) {
    console.error('Failed to load Grok sentiment from localStorage:', error);
    return {};
  }
};

// Save state to localStorage
const saveToLocalStorage = (state: GrokSentimentState) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      byTicker: state.byTicker,
      lastUpdated: state.lastUpdated,
    }));
  } catch (error) {
    console.error('Failed to save Grok sentiment to localStorage:', error);
  }
};

const initialState: GrokSentimentState = {
  byTicker: {},
  loading: false,
  batchLoading: false,
  error: null,
  lastUpdated: {},
  ...loadFromLocalStorage(), // Load persisted data
};

const grokSentimentSlice = createSlice({
  name: 'grokSentiment',
  initialState,
  reducers: {
    setSentimentData: (state, action: PayloadAction<GrokSentimentData & {
      success: boolean;
      error?: string;
      is_cached?: boolean;
      analyzed_at?: string;
      cache_age_hours?: number;
    }>) => {
      const ticker = action.payload.ticker.toUpperCase();
      state.byTicker[ticker] = action.payload;
      // Only update lastUpdated timestamp for fresh (non-cached) data
      if (!action.payload.is_cached) {
        state.lastUpdated[ticker] = Date.now();
      }
      state.loading = false;
      state.error = null;
      saveToLocalStorage(state); // Persist to localStorage
    },
    setBatchSentimentData: (state, action: PayloadAction<GrokSentimentResponse[]>) => {
      action.payload.forEach((result) => {
        if (result.success && result.ticker) {
          const ticker = result.ticker.toUpperCase();
          state.byTicker[ticker] = result as GrokSentimentData & {
            success: boolean;
            is_cached?: boolean;
            analyzed_at?: string;
            cache_age_hours?: number;
          };
          // Only update lastUpdated timestamp for fresh (non-cached) data
          if (!result.is_cached) {
            state.lastUpdated[ticker] = Date.now();
          }
        } else if (result.ticker) {
          const ticker = result.ticker.toUpperCase();
          state.byTicker[ticker] = {
            ...result,
            ticker,
            overall_sentiment: 'neutral',
            key_themes: [],
            notable_investors_or_analysts: [],
            breaking_news_or_catalysts: [],
            sentiment_shift_vs_last_week: '',
            retail_vs_institutional: '',
            hype_assessment: { level: 'medium', type: 'organic' },
            momentum: 'stable',
            summary: '',
          };
        }
      });
      state.batchLoading = false;
      state.error = null;
      saveToLocalStorage(state); // Persist to localStorage
    },
    setSentimentLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setBatchSentimentLoading: (state, action: PayloadAction<boolean>) => {
      state.batchLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setSentimentError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
      state.batchLoading = false;
    },
    clearSentimentData: (state, action: PayloadAction<string>) => {
      const ticker = action.payload.toUpperCase();
      delete state.byTicker[ticker];
      delete state.lastUpdated[ticker];
      saveToLocalStorage(state); // Persist to localStorage
    },
    clearAllSentimentData: (state) => {
      state.byTicker = {};
      state.lastUpdated = {};
      state.error = null;
      saveToLocalStorage(state); // Persist to localStorage
    },
  },
});

export const {
  setSentimentData,
  setBatchSentimentData,
  setSentimentLoading,
  setBatchSentimentLoading,
  setSentimentError,
  clearSentimentData,
  clearAllSentimentData,
} = grokSentimentSlice.actions;

export default grokSentimentSlice.reducer;
